import { Color, FabricText, Gradient, Group, Path, Pattern, Rect, Shadow, util } from 'fabric';
import type { FabricObject } from 'fabric';
import type { FillValue, ToolRecipe } from '../types';
import { tagSmartObject } from '../types';
import { applyFill } from '../fill';
import { registerRecipe } from '../registry';

/**
 * "Art text" — one recipe, ten display-text treatments. Every style is built
 * from stacked/clipped FabricText copies (never subclasses) so the output
 * stays plain fabric JSON. FabricText (not Textbox) keeps every layer's
 * metrics identical — offsets between copies are then pure translation.
 */

/** Options bag accepted by the FabricText constructor (avoids importing prop types). */
type TextOverrides = NonNullable<ConstructorParameters<typeof FabricText>[1]>;

interface ArtTextParams {
  text: string;
  fontFamily: string;
  fontSize: number;
  style: string;
  /** Rich fill (solid / gradient / texture); plain strings stay valid. */
  fill: FillValue;
  accentColor: string;
  depth: number;
  extrudeAngle: number;
  strokeWidth: number;
  textureUrl: string;
  curvature: number;
  gradientAngle: number;
}

const str = (v: unknown, fallback: string): string => (typeof v === 'string' ? v : fallback);
const num = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;

/** The pre-fill-support default solid — applyFill's fallback when a fill can't resolve. */
const FILL_FALLBACK = '#ffd166';

/** Coerce an untrusted param into a FillValue (plain strings pass through unchanged). */
function fillVal(v: unknown, fallback: string): FillValue {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const t = (v as { type?: unknown }).type;
    if (t === 'solid' || t === 'linear-gradient' || t === 'texture') return v as FillValue;
  }
  return fallback;
}

/**
 * Best solid stand-in for a FillValue — for the styles that keep their OWN
 * fill logic (gradient builds its own Gradient; texture builds its own
 * Pattern) and only need a color string out of the param.
 */
function fillColor(fill: FillValue, fallback: string): string {
  if (typeof fill === 'string') return fill;
  switch (fill.type) {
    case 'solid':
      return fill.color;
    case 'linear-gradient':
      return fill.from ?? fill.stops?.[0]?.color ?? fallback;
    default:
      return fallback;
  }
}

/** Untrusted params in (regenerate feeds stored JSON back through here) — coerce everything. */
function readParams(raw: Record<string, unknown>): ArtTextParams {
  return {
    text: str(raw.text, 'BUILD THE FUTURE'),
    fontFamily: str(raw.fontFamily, 'Arial Black'),
    fontSize: Math.max(4, num(raw.fontSize, 96)),
    style: str(raw.style, 'block-extrude'),
    fill: fillVal(raw.fill, FILL_FALLBACK),
    accentColor: str(raw.accentColor, '#1d3557'),
    depth: Math.max(1, Math.round(num(raw.depth, 3))),
    extrudeAngle: num(raw.extrudeAngle, 45),
    strokeWidth: Math.max(0, num(raw.strokeWidth, 8)),
    textureUrl: str(raw.textureUrl, ''),
    curvature: Math.max(-100, Math.min(100, num(raw.curvature, 40))),
    gradientAngle: num(raw.gradientAngle, 90),
  };
}

/** All copies must share text/font/size so layer metrics line up exactly. */
function makeText(p: ArtTextParams, overrides: TextOverrides = {}): FabricText {
  return new FabricText(p.text, {
    fontFamily: p.fontFamily,
    fontSize: p.fontSize,
    left: 0,
    top: 0,
    ...overrides,
  });
}

function withAlpha(color: string, alpha: number): string {
  return new Color(color).setAlpha(alpha).toRgba();
}

/** Children are back-to-front; the group is what the caller places. */
function wrap(children: FabricObject[]): Group {
  const group = new Group(children);
  group.set({ left: 0, top: 0 });
  return group;
}

async function blockExtrude(p: ArtTextParams): Promise<Group> {
  // Per-layer offset scales with the type size so depth reads at any scale.
  const step = Math.max(1, p.fontSize * 0.045);
  const rad = util.degreesToRadians(p.extrudeAngle);
  const dx = Math.cos(rad) * step;
  const dy = Math.sin(rad) * step;
  const layers: FabricObject[] = [];
  for (let i = p.depth; i >= 1; i--) {
    layers.push(makeText(p, { fill: p.accentColor, left: dx * i, top: dy * i }));
  }
  const front = makeText(p);
  await applyFill(front, p.fill, FILL_FALLBACK);
  layers.push(front);
  return wrap(layers);
}

async function sticker(p: ArtTextParams): Promise<Group> {
  // paintFirst keeps the white border under the fill so thin glyphs stay legible.
  const face = makeText(p, {
      stroke: '#ffffff',
      strokeWidth: Math.max(2, p.strokeWidth),
      strokeLineJoin: 'round',
      paintFirst: 'stroke',
      shadow: new Shadow({
        color: 'rgba(0,0,0,0.35)',
        blur: p.fontSize * 0.12,
        offsetX: 0,
        offsetY: p.fontSize * 0.06,
      }),
  });
  await applyFill(face, p.fill, FILL_FALLBACK);
  return wrap([face]);
}

function neon(p: ArtTextParams): Group {
  // Shadows don't stack on one object, so the halo is two extra copies with
  // successively tighter blurs under a white-hot core.
  const tube = Math.max(2, p.strokeWidth * 0.5);
  const glow = (blur: number) =>
    makeText(p, {
      fill: '',
      stroke: p.accentColor,
      strokeWidth: tube,
      strokeLineJoin: 'round',
      shadow: new Shadow({ color: p.accentColor, blur, offsetX: 0, offsetY: 0 }),
    });
  return wrap([
    glow(p.fontSize * 0.55),
    glow(p.fontSize * 0.22),
    makeText(p, {
      fill: withAlpha(p.accentColor, 0.15),
      stroke: '#ffffff',
      strokeWidth: tube,
      strokeLineJoin: 'round',
    }),
  ]);
}

async function chromatic(p: ArtTextParams): Promise<Group> {
  // Fixed red/cyan plates — the misregistered-print look depends on those hues.
  const o = Math.max(1.5, p.fontSize * 0.03);
  const front = makeText(p);
  await applyFill(front, p.fill, FILL_FALLBACK);
  return wrap([
    makeText(p, { fill: '#ff0044', left: -o, top: -o * 0.5 }),
    makeText(p, { fill: '#00e5ff', left: o, top: o * 0.5 }),
    front,
  ]);
}

async function twoTone(p: ArtTextParams): Promise<Group> {
  const probe = makeText(p);
  const w = probe.width;
  const h = probe.height;
  // clipPath coords are relative to the object's center; rects overshoot by
  // 1px horizontally so antialiased glyph edges aren't shaved at the seam.
  const topHalf = new Rect({ left: -w / 2 - 1, top: -h / 2 - 1, width: w + 2, height: h / 2 + 1 });
  const bottomHalf = new Rect({ left: -w / 2 - 1, top: 0, width: w + 2, height: h / 2 + 1 });
  const top = makeText(p, { clipPath: topHalf });
  await applyFill(top, p.fill, FILL_FALLBACK);
  return wrap([
    top,
    makeText(p, { fill: p.accentColor, clipPath: bottomHalf }),
  ]);
}

function gradientText(p: ArtTextParams): Group {
  const rad = util.degreesToRadians(p.gradientAngle);
  const dx = Math.cos(rad) / 2;
  const dy = Math.sin(rad) / 2;
  // Percentage units keep the gradient valid if the user later edits the text.
  const fill = new Gradient({
    type: 'linear',
    gradientUnits: 'percentage',
    coords: { x1: 0.5 - dx, y1: 0.5 - dy, x2: 0.5 + dx, y2: 0.5 + dy },
    colorStops: [
      // This style owns its gradient — a rich fill param contributes only a color.
      { offset: 0, color: fillColor(p.fill, FILL_FALLBACK) },
      { offset: 1, color: p.accentColor },
    ],
  });
  return wrap([makeText(p, { fill })]);
}

async function texture(p: ArtTextParams): Promise<Group> {
  // generate() must never throw for a bad/blocked URL — fall back to solid fill.
  // This style owns its Pattern; the fill param contributes only a solid color.
  const solid = fillColor(p.fill, FILL_FALLBACK);
  let fill: TextOverrides['fill'] = solid;
  if (p.textureUrl) {
    try {
      const img = await util.loadImage(p.textureUrl, { crossOrigin: 'anonymous' });
      fill = new Pattern({ source: img, repeat: 'repeat' });
    } catch {
      fill = solid;
    }
  }
  return wrap([makeText(p, { fill })]);
}

function outline(p: ArtTextParams): Group {
  return wrap([
    makeText(p, {
      fill: '',
      stroke: p.accentColor,
      strokeWidth: Math.max(1, p.strokeWidth * 0.5),
      strokeLineJoin: 'round',
    }),
  ]);
}

async function varsity(p: ArtTextParams): Promise<Group> {
  // The back copy's oversized stroke pokes out past the front copy's white
  // stroke, producing the second border ring without any path math.
  const mid = Math.max(2, p.strokeWidth);
  const front = makeText(p, {
    stroke: '#ffffff',
    strokeWidth: mid,
    strokeLineJoin: 'round',
    paintFirst: 'stroke',
  });
  await applyFill(front, p.fill, FILL_FALLBACK);
  return wrap([
    makeText(p, {
      fill: p.accentColor,
      stroke: p.accentColor,
      strokeWidth: mid * 2.5,
      strokeLineJoin: 'round',
      paintFirst: 'stroke',
    }),
    front,
  ]);
}

async function arch(p: ArtTextParams): Promise<Group> {
  if (p.curvature === 0) {
    const flat = makeText(p);
    await applyFill(flat, p.fill, FILL_FALLBACK);
    return wrap([flat]);
  }
  const w = makeText(p).width;
  // Quadratic arc spanning the text width; peak height = curvature% of a
  // capped sagitta. Control point sits at 2x the peak (Bezier midpoint rule).
  const sag = (p.curvature / 100) * w * 0.25;
  const arc = new Path(`M 0 0 Q ${w / 2} ${-sag * 2} ${w} 0`, { visible: false });
  // The arc is longer than its chord — offset the start so the text centers.
  const segments = util.getPathSegmentsInfo(arc.path);
  const arcLength = segments[segments.length - 1].length;
  const curved = makeText(p, {
    path: arc,
    pathAlign: 'center',
    pathStartOffset: Math.max(0, (arcLength - w) / 2),
  });
  await applyFill(curved, p.fill, FILL_FALLBACK);
  return wrap([curved]);
}

export const artText: ToolRecipe = {
  id: 'art-text',
  domain: 'display',
  tags: ['text-art', 'headline', 'title', 'word-art', '3d-text', 'display'],
  name: 'Art Text',
  category: 'text',
  version: 1,
  description:
    'Display headline text with layered effects: 3D extrude, sticker, neon, chromatic, two-tone, gradient, texture, outline, varsity, or arched.',
  defaults: {
    text: 'BUILD THE FUTURE',
    fontFamily: 'Arial Black',
    fontSize: 96,
    style: 'block-extrude',
    fill: '#ffd166',
    accentColor: '#1d3557',
    depth: 3,
    extrudeAngle: 45,
    strokeWidth: 8,
    textureUrl: '',
    curvature: 40,
    gradientAngle: 90,
  },
  schema: [
    { key: 'text', label: 'Text', type: 'text' },
    { key: 'fontFamily', label: 'Font', type: 'font' },
    { key: 'fontSize', label: 'Font size', type: 'number', min: 8, max: 600, step: 1 },
    {
      key: 'style',
      label: 'Style',
      type: 'select',
      options: [
        { value: 'block-extrude', label: '3D block extrude' },
        { value: 'sticker', label: 'Sticker' },
        { value: 'neon', label: 'Neon' },
        { value: 'chromatic', label: 'Chromatic offset' },
        { value: 'two-tone', label: 'Two tone' },
        { value: 'gradient', label: 'Gradient' },
        { value: 'texture', label: 'Texture fill' },
        { value: 'outline', label: 'Outline' },
        { value: 'varsity', label: 'Varsity' },
        { value: 'arch', label: 'Arch' },
      ],
    },
    {
      key: 'fill',
      label: 'Fill',
      type: 'fill',
      help: 'Solid, gradient, or texture. Gradient/texture fills apply in the block extrude, sticker, chromatic, two-tone, varsity and arch styles; the gradient, texture, neon and outline styles color themselves.',
    },
    {
      key: 'accentColor',
      label: 'Accent color',
      type: 'color',
      help: 'Extrude, stroke, glow, gradient end, or bottom tone — depends on style.',
    },
    {
      key: 'depth',
      label: 'Extrude depth',
      type: 'number',
      min: 1,
      max: 12,
      step: 1,
      help: 'Number of stacked layers (block extrude).',
    },
    {
      key: 'extrudeAngle',
      label: 'Extrude angle',
      type: 'number',
      min: 0,
      max: 360,
      step: 5,
      advanced: true,
      help: 'Direction the block extrude recedes, in degrees.',
    },
    {
      key: 'strokeWidth',
      label: 'Stroke width',
      type: 'number',
      min: 0,
      max: 60,
      step: 1,
      advanced: true,
    },
    {
      key: 'textureUrl',
      label: 'Texture image',
      type: 'image',
      advanced: true,
      help: 'Only used by the "Texture fill" style; falls back to the fill color. For textures in other styles, use the Fill picker instead.',
    },
    {
      key: 'curvature',
      label: 'Curvature',
      type: 'number',
      min: -100,
      max: 100,
      step: 5,
      help: 'Arch bend: negative frowns, 0 is straight.',
    },
    {
      key: 'gradientAngle',
      label: 'Gradient angle',
      type: 'number',
      min: 0,
      max: 360,
      step: 5,
      advanced: true,
    },
  ],
  async generate(params) {
    const p = readParams(params);
    let group: Group;
    switch (p.style) {
      case 'sticker':
        group = await sticker(p);
        break;
      case 'neon':
        group = neon(p);
        break;
      case 'chromatic':
        group = await chromatic(p);
        break;
      case 'two-tone':
        group = await twoTone(p);
        break;
      case 'gradient':
        group = gradientText(p);
        break;
      case 'texture':
        group = await texture(p);
        break;
      case 'outline':
        group = outline(p);
        break;
      case 'varsity':
        group = await varsity(p);
        break;
      case 'arch':
        group = await arch(p);
        break;
      case 'block-extrude':
      default:
        group = await blockExtrude(p);
        break;
    }
    return tagSmartObject(group, artText, params);
  },
};

registerRecipe(artText);
